import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowClockwiseIcon, PaperPlaneRightIcon, ShieldCheckIcon, UserIcon } from '@phosphor-icons/react';
import { ApiError, Button, Input, StatusChip, cn, type Tone } from '@smena/ui';
import { fetchSandbox, resetSandbox, sendSandboxMessage, type SandboxReply } from './api';

/**
 * Демо-чат: клиент пишет агенту от лица гостя и сразу видит ответ.
 *
 * Раньше на этом шаге стояли два абзаца текста: «откройте гостевой чат по ссылке из брони».
 * То есть чтобы проверить бота, надо было сначала завести бронь, найти приглашение и уйти
 * из мастера - проверкой это не было.
 *
 * Работает настоящий конвейер, а не заглушка: тот же разбор намерения, та же база знаний,
 * те же правила эскалации. Изоляция только во внешних эффектах - оператору ничего не летит,
 * в ленте диалогов чат не появляется, брони не создаются.
 */
export function SandboxChat() {
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const [last, setLast] = useState<SandboxReply | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  const q = useQuery({ queryKey: ['sandbox'], queryFn: fetchSandbox });
  const history = q.data?.history ?? [];

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [history.length]);

  const sendMut = useMutation({
    mutationFn: (t: string) => sendSandboxMessage(t),
    onSuccess: (r) => {
      setLast(r);
      setText('');
      qc.setQueryData(['sandbox'], { history: r.history });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Агент не ответил'),
  });

  const resetMut = useMutation({
    mutationFn: resetSandbox,
    onSuccess: () => {
      setLast(null);
      qc.setQueryData(['sandbox'], { history: [] });
      toast.success('Диалог очищен');
    },
  });

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-start gap-2 rounded-md border border-accent/30 bg-accent/8 p-3 text-xs">
        <ShieldCheckIcon weight="fill" className="mt-0.5 size-4 shrink-0 text-accent-fg" />
        <div>
          <div className="font-semibold text-text">Тестовый режим</div>
          Отвечает тот же агент, что и гостям: та же анкета объекта, та же база знаний, те же
          правила. Наружу ничего не уходит - оператору не приходят оповещения, диалог не
          появляется в разделе «Диалоги», бронь не создаётся.
        </div>
      </div>

      <div className="flex min-h-64 flex-col gap-2 rounded-md border border-line bg-panel2/40 p-3">
        {q.isLoading ? (
          <p className="text-xs text-muted">Загружаем…</p>
        ) : history.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-2 py-6 text-center">
            <UserIcon weight="bold" className="size-6 text-muted" />
            <p className="text-sm text-muted">Напишите как гость - и посмотрите, что ответит агент.</p>
            <div className="flex flex-wrap justify-center gap-2">
              {SUGGESTIONS.map((s) => (
                <Button
                  key={s}
                  variant="secondary"
                  size="sm"
                  disabled={sendMut.isPending}
                  onClick={() => sendMut.mutate(s)}
                >
                  {s}
                </Button>
              ))}
            </div>
          </div>
        ) : (
          <>
            {history.map((m, i) => (
              <div
                key={i}
                className={cn(
                  'max-w-[85%] rounded-lg px-3 py-2 text-sm',
                  m.role === 'guest'
                    ? 'self-end bg-accent text-accent-contrast'
                    : 'self-start border border-line bg-card',
                )}
              >
                {m.text}
              </div>
            ))}
            <div ref={endRef} />
          </>
        )}
        {sendMut.isPending ? <div className="self-start text-xs text-muted">Агент думает…</div> : null}
      </div>

      {last ? (
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted">
          <StatusChip tone={decisionTone(last.decision)}>{decisionLabel(last.decision)}</StatusChip>
          {last.intent ? <span>намерение: {last.intent}</span> : null}
          {last.escalationReason ? (
            <span className="text-amber">причина: {last.escalationReason}</span>
          ) : null}
        </div>
      ) : null}

      <form
        className="flex flex-col gap-2 sm:flex-row"
        onSubmit={(e) => {
          e.preventDefault();
          const t = text.trim();
          if (!t || sendMut.isPending) return;
          sendMut.mutate(t);
        }}
      >
        <Input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Например: во сколько заезд?"
          disabled={sendMut.isPending}
        />
        <div className="flex shrink-0 gap-2">
          <Button type="submit" size="sm" disabled={!text.trim() || sendMut.isPending}>
            <PaperPlaneRightIcon weight="bold" className="size-4" />
            Отправить
          </Button>
          {history.length ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={resetMut.isPending}
              onClick={() => resetMut.mutate()}
            >
              <ArrowClockwiseIcon weight="bold" className="size-4" />
              Заново
            </Button>
          ) : null}
        </div>
      </form>
    </div>
  );
}

/** Вопросы, на которые агент обязан отвечать сам - если нет, значит анкета не дозаполнена. */
const SUGGESTIONS = ['Во сколько заезд?', 'Какой пароль от Wi-Fi?', 'Как попасть в квартиру?'];

function decisionTone(d: SandboxReply['decision']): Tone {
  if (d === 'auto') return 'green';
  if (d === 'draft') return 'accent';
  return 'amber';
}

function decisionLabel(d: SandboxReply['decision']): string {
  if (d === 'auto') return 'ответил сам';
  if (d === 'draft') return 'черновик - в бою его подтверждает оператор';
  return 'позвал оператора';
}
